import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useSEO } from "../lib/seo";
import { Reveal } from "../lib/reveal";
import { CATEGORY_LABELS } from "../data/content";

const SECTIONS = [
  { key: "context", label: "Contexte" },
  { key: "client_need", label: "Besoin du client" },
  { key: "works", label: "Travaux réalisés" },
  { key: "constraints", label: "Contraintes techniques" },
  { key: "solutions", label: "Solutions apportées" },
];

const GALLERIES = [
  { key: "gallery_before", label: "Avant" },
  { key: "gallery_during", label: "Chantier" },
  { key: "gallery_after", label: "Après" },
];

const ProjectDetailPage = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api
      .get(`/projects/${slug}`)
      .then((res) => {
        setProject(res.data);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  }, [slug]);

  useSEO(
    project ? `${project.title} — ${project.city} | BÂTI 360` : "Réalisation — BÂTI 360",
    project ? project.description : "Réalisation BÂTI 360, entreprise de rénovation tous corps d'état à Paris et Île-de-France."
  );

  if (status === "loading") {
    return <div className="flex min-h-[60vh] items-center justify-center pt-20"><div className="h-10 w-10 animate-pulse bg-bati-blue" /></div>;
  }

  if (status === "error") {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 pt-20" data-testid="project-not-found">
        <h1 className="font-display text-3xl font-bold text-bati-ink">Projet introuvable</h1>
        <Link to="/realisations" className="bg-bati-blue px-6 py-3 text-[13px] font-bold uppercase tracking-[0.12em] text-white hover:bg-bati-sky">
          Voir toutes les réalisations
        </Link>
      </div>
    );
  }

  const meta = [
    { label: "Lieu", value: project.city },
    { label: "Type de chantier", value: CATEGORY_LABELS[project.category] || project.category },
    { label: "Surface", value: project.surface },
    { label: "Nature des travaux", value: project.work_types },
  ];

  const labels = project.gallery_labels || {};
  const galleries = GALLERIES.map((g) => ({ ...g, label: labels[g.key] || g.label }));
  const videos = project.videos?.length ? project.videos : project.video ? [project.video] : [];

  return (
    <div className="pt-20 md:pt-24">
      <header className="relative flex min-h-[60vh] items-end overflow-hidden bg-bati-ink">
        <img src={project.cover_image} alt={project.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-14 pt-32 lg:px-8">
          <Link to="/realisations" data-testid="back-to-projects" className="mb-6 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em] text-white/70 transition-colors hover:text-bati-sky">
            <ArrowLeft className="h-4 w-4" /> Réalisations
          </Link>
          <Reveal>
            <p className="mb-3 text-[12px] font-bold uppercase tracking-[0.22em] text-bati-sky">
              {CATEGORY_LABELS[project.category] || project.category} — {project.city}
            </p>
            <h1 className="max-w-4xl font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
              {project.title}
            </h1>
          </Reveal>
        </div>
      </header>

      <div className="border-b border-black/10 bg-bati-mist">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-black/10 px-0 lg:grid-cols-4" data-testid="project-meta">
          {meta.map((m) => (
            <div key={m.label} className="bg-bati-mist p-6">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-bati-sky">{m.label}</p>
              <p className="mt-2 text-sm font-semibold leading-snug text-bati-ink">{m.value || "—"}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl space-y-16 px-5 lg:px-8">
          {project.example && (
            <div className="border border-black/10 bg-bati-mist px-5 py-4 text-xs leading-relaxed text-bati-ink/60" data-testid="project-example-notice">
              Projet présenté à titre d'exemple de savoir-faire — les photographies sont des visuels d'illustration en attente des photos réelles BÂTI 360.
            </div>
          )}
          {project.description && (
            <Reveal>
              <p className="max-w-3xl font-display text-xl font-medium leading-relaxed text-bati-ink sm:text-2xl">
                {project.description}
              </p>
            </Reveal>
          )}
          {SECTIONS.filter((s) => project[s.key]).map((s) => (
            <Reveal key={s.key}>
              <div className="grid gap-6 border-t border-black/10 pt-10 lg:grid-cols-12" data-testid={`project-section-${s.key}`}>
                <h2 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-bati-blue lg:col-span-4">{s.label}</h2>
                <p className="text-base leading-relaxed text-bati-ink/70 lg:col-span-8">{project[s.key]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {galleries.filter((g) => (project[g.key] || []).length > 0).map((g) => (
        <section key={g.key} className="border-t border-black/10 bg-white py-16 lg:py-24" data-testid={`gallery-${g.key}`}>
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <h2 className="mb-10 flex items-center gap-4 font-display text-3xl font-extrabold tracking-tight text-bati-ink">
                <span className="h-px w-10 bg-bati-sky" /> {g.label}
              </h2>
            </Reveal>
            <div className={`grid gap-5 ${(project[g.key] || []).length === 1 ? "" : "md:grid-cols-2"}`}>
              {(project[g.key] || []).map((src, i) => (
                <Reveal key={src + i} delay={i * 0.08}>
                  <div className="overflow-hidden">
                    <img src={src} alt={`${project.title} — ${g.label} ${i + 1}`} loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-700 hover:scale-[1.03]" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      {videos.length > 0 && (
        <section className="border-t border-black/10 bg-bati-mist py-16 lg:py-24" data-testid="project-video-section">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <h2 className="mb-4 flex items-center gap-4 font-display text-3xl font-extrabold tracking-tight text-bati-ink">
                <span className="h-px w-10 bg-bati-sky" /> Le chantier en vidéo{videos.length > 1 ? "s" : ""}
              </h2>
              <p className="mb-10 max-w-xl text-sm leading-relaxed text-bati-ink/60">
                Suivez l'avancement du chantier en images animées. Lancez la lecture quand vous le souhaitez.
              </p>
            </Reveal>
            <div className={videos.length > 1 ? "grid items-start gap-6 sm:grid-cols-2" : ""}>
              {videos.map((src) => (
                <Reveal key={src}>
                  <div className={`overflow-hidden border border-black/10 bg-black ${videos.length > 1 ? "" : "mx-auto max-w-[420px]"}`}>
                    <video
                      data-testid="project-video"
                      src={src}
                      poster={src.replace(".mp4", "-poster.webp")}
                      controls
                      playsInline
                      preload="metadata"
                      className="h-auto w-full"
                    >
                      Votre navigateur ne prend pas en charge la lecture vidéo.
                    </video>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-bati-blue py-20 text-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-5 lg:px-8">
          <h2 className="max-w-xl font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
            Un projet similaire ? Parlons-en.
          </h2>
          <Link
            to="/contact"
            data-testid="project-cta-devis"
            className="group flex items-center gap-3 bg-white px-8 py-4 text-[13px] font-bold uppercase tracking-[0.12em] text-bati-blue transition-colors duration-300 hover:bg-bati-sky hover:text-white"
          >
            Demander un devis
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetailPage;
